import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Siren, X, Phone, MessageCircle, CheckCircle2, BellRing } from "lucide-react";
import api from "../../api";

// "Crisis protocol" (backend clinical/safety/crisis.py): emergency numbers first, the
// protocol as short numbered steps (with what to say), then one tap to alert
// the patient's care team, supervisors and admins. The popup names who the
// alert will reach before it's sent. The app alert always goes; email, SMS and
// WhatsApp are optional. WhatsApp goes from the clinician's own WhatsApp, not
// the platform's number: after alerting, each person gets a WhatsApp button
// that opens it with the message ready (backend raise_alert skips the API send).
// With nobody else to tell (a solo practitioner), the box becomes "you're the
// only clinician here": the patient's and emergency contact's numbers, and a
// button that records the crisis on the file without claiming anyone was told.
const CHANNELS = [
  { key: "email", label: "Email" },
  { key: "sms", label: "SMS" },
  { key: "whatsapp", label: "WhatsApp" },
];

const waNumber = (phone) => {
  const d = (phone || "").replace(/\D/g, "");
  return d.length === 10 ? `91${d}` : d;
};

export default function CrisisButton({ patientId, patientName, compact = false, onRaised }) {
  const [open, setOpen] = useState(false);
  const [protocol, setProtocol] = useState(null);
  const [summary, setSummary] = useState("");
  const [channels, setChannels] = useState({ email: true, sms: true, whatsapp: true });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [reach, setReach] = useState(null); // {contacts, patient_phone, emergency_contact}; false if it couldn't load
  const first = (patientName || "the patient").split(" ")[0];
  const defaultMessage = `Urgent: ${first} may be at risk of harming themselves. I'm with them now and following the crisis protocol. Please call me as soon as you see this.`;

  const show = () => {
    setOpen(true);
    setSent(null);
    if (!summary) setSummary(defaultMessage);
    if (!protocol) api.getCrisisProtocol().then((r) => setProtocol(r.data)).catch(() => setProtocol({ steps: [], lines: [] }));
    api.getCrisisRecipients(patientId).then((r) => setReach(r.data)).catch(() => setReach(false));
  };
  const close = () => { setOpen(false); setSent(null); };

  // Only once we know for sure there's nobody else; if the lookup failed, keep the normal alert.
  const solo = reach && reach.contacts?.length === 0;
  const names = (list) => list.map((c) => c.name).join(", ");
  // Only the channels the platform has switched on for this clinic; none until we know.
  const offered = CHANNELS.filter((c) => (reach?.channels || []).includes(c.key));

  const alertNow = async () => {
    setSending(true);
    try {
      const chosen = solo ? ["in_app"] : ["in_app", ...offered.filter((c) => channels[c.key]).map((c) => c.key)];
      const res = await api.raiseCrisisAlert({ patient_id: patientId, summary, channels: chosen });
      setSent({ ...res.data, chosen, at: new Date() });
      setSummary(defaultMessage);
      if (onRaised) onRaised(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || (solo ? "Couldn't save it to the record. Carry on with the protocol and note it in the session." : "Couldn't send the alert. Call the care team directly."));
    } finally {
      setSending(false);
    }
  };

  const waText = encodeURIComponent(
    `URGENT crisis alert - ${patientName || "patient"}\n${sent?.summary || defaultMessage}\nOpen their record in MindConnect: ${window.location.origin}/patient/${patientId}`
  );

  return (
    <>
      <button type="button" onClick={show}
        className={`inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 rounded-lg border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 font-bold transition-colors ${compact ? "px-2.5 py-1.5 text-xs" : "px-3.5 py-2 text-xs"}`}>
        <Siren className="w-3.5 h-3.5" /> Crisis protocol
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-3 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="crisis-title">
          <div className="w-full max-w-xl max-h-[94vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between bg-rose-600 px-5 py-3 text-white">
              <h2 id="crisis-title" className="flex items-center gap-2 text-lg font-bold">
                <Siren className="w-5 h-5" /> Crisis protocol{patientName ? ` · ${patientName}` : ""}
              </h2>
              <button type="button" onClick={close} className="p-1 rounded-full hover:bg-white/20" aria-label="Close"><X className="w-5 h-5" /></button>
            </div>

            {sent ? (
              <div className="p-5 space-y-4">
                {sent.contacts?.length > 0 ? (
                  <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                    <div className="text-sm text-emerald-900">
                      <p className="font-bold text-base">Alert sent at {sent.at.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}</p>
                      <p>To {names(sent.contacts)} - in the app{sent.chosen.includes("email") ? ", by email" : ""}{sent.chosen.includes("sms") ? " and by SMS (once SMS is set up)" : ""}. The alert is logged on the patient's record.</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4">
                    <CheckCircle2 className="w-6 h-6 text-amber-600 shrink-0" />
                    <div className="text-sm text-amber-900">
                      <p className="font-bold text-base">Recorded on the patient's file at {sent.at.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}</p>
                      <p>Nobody else was notified - there's no one else in the clinic to tell, so you're handling it. Once the patient is safe, close it from their file with what was done.</p>
                    </div>
                  </div>
                )}
                {sent.contacts?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-800">Reach them directly</p>
                    {sent.contacts.map((c) => (
                      <div key={c.user_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2">
                        <span className="text-sm font-medium text-gray-900">{c.name}</span>
                        {c.phone ? (
                          <span className="flex gap-2">
                            <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 rounded-lg bg-gray-900 text-white text-xs font-bold px-3 py-1.5"><Phone className="w-3.5 h-3.5" /> Call</a>
                            {sent.chosen.includes("whatsapp") && (
                              <a href={`https://wa.me/${waNumber(c.phone)}?text=${waText}`} target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white text-xs font-bold px-3 py-1.5"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
                            )}
                          </span>
                        ) : <span className="text-xs text-gray-500">No phone on file</span>}
                      </div>
                    ))}
                    {sent.chosen.includes("whatsapp") && (
                      <p className="text-xs text-gray-500">WhatsApp opens with the message written - just press send.</p>
                    )}
                  </div>
                )}
                <button type="button" onClick={() => setSent(null)} className="w-full rounded-xl border border-gray-300 py-2.5 text-sm font-semibold">Back to the protocol</button>
              </div>
            ) : (
              <div className="p-5 space-y-5">
                <div className="grid gap-2 sm:grid-cols-2">
                  {(protocol?.lines || []).map((l) => (
                    <a key={l.number} href={`tel:${l.number}`} className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 p-3 hover:bg-rose-100">
                      <span className="p-2 rounded-full bg-rose-600 text-white"><Phone className="w-4 h-4" /></span>
                      <span>
                        <span className="block text-xl font-extrabold text-gray-900">{l.number}</span>
                        <span className="block text-xs text-gray-600">{l.name}{l.alt ? ` · ${l.alt}` : ""}</span>
                      </span>
                    </a>
                  ))}
                </div>

                {!protocol ? <p className="text-sm text-gray-500">Loading…</p> : protocol.steps?.length > 0 && (
                  <ol className="space-y-3">
                    {protocol.steps.map((s, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-rose-600 text-white text-sm font-extrabold flex items-center justify-center">{i + 1}</span>
                        <div className="pt-0.5">
                          <p className="text-base font-bold text-gray-900">{s.title}</p>
                          <p className="text-sm text-gray-700">{s.text}</p>
                          {s.say && <p className="mt-1 rounded-lg bg-sky-50 border-l-4 border-sky-400 px-3 py-1.5 text-sm text-sky-900"><span className="font-semibold">Say: </span>“{s.say}”</p>}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}

                {solo ? (
                <div className="rounded-xl border-2 border-amber-300 bg-amber-50/40 p-4 space-y-3">
                  <p className="text-sm font-bold text-gray-900">You're the only clinician here</p>
                  <p className="text-xs text-gray-600">There's nobody else in your clinic to alert, so stay with {first} and use the numbers above if they're in immediate danger. Reach someone close to them:</p>
                  {[
                    reach.emergency_contact && { key: "ec", label: `${reach.emergency_contact.name || "Emergency contact"}${reach.emergency_contact.relation ? ` (${reach.emergency_contact.relation})` : ""}`, phone: reach.emergency_contact.phone, wa: true },
                    reach.patient_phone && { key: "pt", label: `${patientName || "Patient"} (patient)`, phone: reach.patient_phone },
                  ].filter(Boolean).map((c) => (
                    <div key={c.key} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                      <span className="text-sm font-medium text-gray-900">{c.label}</span>
                      <span className="flex gap-2">
                        <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 rounded-lg bg-gray-900 text-white text-xs font-bold px-3 py-1.5"><Phone className="w-3.5 h-3.5" /> Call</a>
                        {c.wa && (
                          <a href={`https://wa.me/${waNumber(c.phone)}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white text-xs font-bold px-3 py-1.5"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
                        )}
                      </span>
                    </div>
                  ))}
                  {!reach.emergency_contact && <p className="text-xs text-gray-500">No emergency contact on file - add one on the patient's profile when you can.</p>}
                  <p className="text-xs text-gray-500">What's happening (saved on the record):</p>
                  <textarea value={summary} onChange={(e) => setSummary(e.target.value)} aria-label="What's happening"
                    className="w-full rounded-lg border border-gray-300 p-2 text-sm min-h-[80px]" />
                  <button type="button" onClick={alertNow} disabled={sending}
                    className="w-full rounded-xl bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-base font-extrabold py-3.5">
                    {sending ? "Saving…" : "Record this crisis on the patient's file"}
                  </button>
                  <p className="text-xs text-gray-500">Nobody is notified. It's kept on the record and you close it once the patient is safe.</p>
                </div>
                ) : (
                // Laid out like a message: who it goes to, what it says, how it's sent,
                // then one button. Only the send button looks like a button.
                <section className="rounded-xl border border-gray-200 bg-gray-50/60" aria-labelledby="crisis-alert-heading">
                  <div className="px-4 pt-4 pb-3">
                    <h3 id="crisis-alert-heading" className="flex items-center gap-2.5 text-base font-bold text-gray-900">
                      <span aria-hidden="true" className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-100 text-rose-600"><BellRing className="w-4 h-4" /></span>
                      Alert the care team
                    </h3>
                  </div>

                  <div className="flex items-start gap-3 border-t border-gray-200 px-4 py-3">
                    <span className="w-16 shrink-0 pt-0.5 text-xs font-semibold text-gray-500">To</span>
                    {reach?.contacts?.length > 0 ? (
                      <ul className="flex flex-wrap gap-x-4 gap-y-2">
                        {reach.contacts.map((c) => (
                          <li key={c.user_id} className="flex items-center gap-2 text-sm font-medium text-gray-900">
                            <span aria-hidden="true" className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-100 text-[10px] font-bold text-rose-700">
                              {(c.name || "?").replace(/^Dr\.?\s+/i, "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                            </span>
                            {c.name}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-700">{reach === null ? "Checking…" : "The patient's clinicians, their supervisors and, if needed, the clinic admins."}</p>
                    )}
                  </div>

                  <div className="flex items-start gap-3 border-t border-gray-200 px-4 py-3">
                    <span className="w-16 shrink-0 pt-2 text-xs font-semibold text-gray-500">Message</span>
                    <div className="flex-1 min-w-0">
                      <textarea value={summary} onChange={(e) => setSummary(e.target.value)} aria-label="Alert message" rows={3}
                        className="w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm leading-relaxed focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-100" />
                      <p className="mt-1 text-xs text-gray-500">Add what matters if you can, e.g. "active plan, 112 called".</p>
                    </div>
                  </div>

                  {offered.length > 0 && (
                  <div className="flex items-start gap-3 border-t border-gray-200 px-4 py-3">
                    <span className="w-16 shrink-0 text-xs font-semibold text-gray-500">Also by</span>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                      {offered.map((c) => (
                        <label key={c.key} className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-800 normal-case font-sans tracking-normal">
                          <input type="checkbox" checked={channels[c.key]} onChange={() => setChannels((p) => ({ ...p, [c.key]: !p[c.key] }))}
                            className="h-4 w-4 rounded border-gray-300 accent-rose-600" />
                          {c.label}
                        </label>
                      ))}
                      <span className="text-xs text-gray-500">In-app alert always goes</span>
                    </div>
                  </div>
                  )}

                  <div className="border-t border-gray-200 p-4">
                    <button type="button" onClick={alertNow} disabled={sending}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-base font-bold py-3.5 shadow-sm">
                      <Siren className="w-5 h-5" />
                      {sending ? "Alerting…" : reach?.contacts?.length > 0 ? `Alert ${reach.contacts.length === 1 ? reach.contacts[0].name : `${reach.contacts.length} people`} now` : "Alert care team now"}
                    </button>
                  </div>
                </section>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
